import { ReactNode, createContext, useContext, useState } from "react";
import { ThemeProvider as StyledThemeProvider } from "styled-components";

import {
  SlateTheme,
  TerraTheme,
  PurpleTheme,
  ForestTheme,
  ThemeType,
  YellowTheme,
  ScarletTheme,
  OceanTheme,
  MintTheme,
} from "./theme/Theme";
import { isImpersonation } from "./redux/store-utils";

interface ThemeContextType {
  changeTheme: ATVoidFunction<ThemeKeys>;
  theme: ThemeType;
  selectedTheme: ThemeKeys;
  allThemes: Record<ThemeKeys, ThemeType>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export enum ThemeKeys {
  PURPLE = "purple",
  SLATE = "slate",
  YELLOW = "yellow",
  TERRA = "terra",
  FOREST = "forest",
  SCARELT = "scarlet",
  OCEAN = "ocean",
  MINT = "mint",
}

const themes = {
  [ThemeKeys.PURPLE]: PurpleTheme,
  [ThemeKeys.SLATE]: SlateTheme,
  [ThemeKeys.YELLOW]: YellowTheme,
  [ThemeKeys.TERRA]: TerraTheme,
  [ThemeKeys.FOREST]: ForestTheme,
  [ThemeKeys.SCARELT]: ScarletTheme,
  [ThemeKeys.OCEAN]: OceanTheme,

  [ThemeKeys.MINT]: MintTheme,
};

const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const getInitialTheme = (): ThemeType => {
    const savedTheme =
      window.localStorage.getItem("atira-space_theme") || ThemeKeys.PURPLE;

    if (Object.values(ThemeKeys).includes(savedTheme as ThemeKeys)) {
      return themes[savedTheme as ThemeKeys];
    }

    return themes[ThemeKeys.PURPLE];
  };

  const [theme, setTheme] = useState<ThemeType>(() => getInitialTheme());

  const changeTheme = (theme: ThemeKeys) => {
    // Change this to store the theme on the user's settings
    setTheme(themes[theme]);
    if (!isImpersonation()) {
      window.localStorage.setItem("atira-space_theme", theme);
    }

    return theme;
  };

  return (
    <ThemeContext.Provider
      value={{
        changeTheme,
        theme,
        selectedTheme:
          (window.localStorage.getItem("atira-space_theme") as ThemeKeys) ||
          ThemeKeys.PURPLE,
        allThemes: themes,
      }}
    >
      <StyledThemeProvider theme={theme}>{children}</StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
